import { supabase } from '../lib/supabase';

export interface HeaderTheme {
  bgGradientFrom: string;
  bgGradientVia: string;
  bgGradientTo: string;
  borderColor: string;
  textColor: string;
  textHoverColor: string;
  activeColor: string;
  activeBgColor: string;
  premiumGradientFrom: string;
  premiumGradientTo: string;
  premiumHoverFrom: string;
  premiumHoverTo: string;
}

export interface FooterTheme {
  bgGradientFrom: string;
  bgGradientVia: string;
  bgGradientTo: string;
  textColor: string;
  textSecondaryColor: string;
  linkColor: string;
  linkHoverColor: string;
  borderColor: string;
}

export interface SiteIdentity {
  faviconUrl: string;
  logoUrl: string;
  siteName: string;
}

// ✅ NEW: Menu Configuration Interface
export interface MenuConfig {
  visible_categories: string[]; // Array of category IDs that should be visible
  category_order: string[]; // Array of category IDs in display order
  extra_links: {
    home: boolean;
    blog: boolean;
    submit: boolean;
    contact: boolean;
  };
  custom_links: Array<{
    id: string;
    label: string;
    url: string;
    icon: string;
  }>;
  max_visible_desktop: number;
  max_visible_mobile: number;
}

export const defaultHeaderTheme: HeaderTheme = {
  bgGradientFrom: '#0c0015',
  bgGradientVia: '#1a0a2e',
  bgGradientTo: '#0c0015',
  borderColor: 'rgba(255, 255, 255, 0.06)',
  textColor: '#d1d5db',
  textHoverColor: '#ffffff',
  activeColor: '#ffffff',
  activeBgColor: 'rgba(255, 255, 255, 0.1)',
  premiumGradientFrom: '#10b981',
  premiumGradientTo: '#14b8a6',
  premiumHoverFrom: '#059669',
  premiumHoverTo: '#0d9488',
};

export const defaultFooterTheme: FooterTheme = {
  bgGradientFrom: '#0f172a',
  bgGradientVia: '#1e293b',
  bgGradientTo: '#0f172a',
  textColor: '#ffffff',
  textSecondaryColor: '#9ca3af',
  linkColor: '#9ca3af',
  linkHoverColor: '#f43f5e',
  borderColor: 'rgba(255, 255, 255, 0.1)',
};

export const defaultSiteIdentity: SiteIdentity = {
  faviconUrl: 'https://public.readdy.ai/ai/img_res/94edb1e7-8dbb-4253-a584-5ebc58a4a114.png',
  logoUrl: 'https://public.readdy.ai/ai/img_res/94edb1e7-8dbb-4253-a584-5ebc58a4a114.png',
  siteName: 'KanalRehberi.com',
};

// ✅ NEW: Default Menu Configuration
export const defaultMenuConfig: MenuConfig = {
  visible_categories: [], // Empty means all categories are visible
  category_order: [], // Empty means use database order
  extra_links: {
    home: true,
    blog: true,
    submit: true,
    contact: true,
  },
  custom_links: [],
  max_visible_desktop: 10,
  max_visible_mobile: 8,
};

export const themePresets = {
  header: {
    'dark-purple': {
      name: 'Koyu Mor',
      theme: defaultHeaderTheme,
    },
    'midnight-blue': {
      name: 'Midnight Blue',
      theme: {
        bgGradientFrom: '#0a1628',
        bgGradientVia: '#1e3a5f',
        bgGradientTo: '#0a1628',
        borderColor: 'rgba(59, 130, 246, 0.2)',
        textColor: '#cbd5e1',
        textHoverColor: '#ffffff',
        activeColor: '#ffffff',
        activeBgColor: 'rgba(59, 130, 246, 0.2)',
        premiumGradientFrom: '#3b82f6',
        premiumGradientTo: '#2563eb',
        premiumHoverFrom: '#2563eb',
        premiumHoverTo: '#1d4ed8',
      },
    },
    'red-black': {
      name: 'Kırmızı-Siyah',
      theme: {
        bgGradientFrom: '#0f0000',
        bgGradientVia: '#2d0a0a',
        bgGradientTo: '#0f0000',
        borderColor: 'rgba(239, 68, 68, 0.2)',
        textColor: '#d1d5db',
        textHoverColor: '#ffffff',
        activeColor: '#ffffff',
        activeBgColor: 'rgba(239, 68, 68, 0.2)',
        premiumGradientFrom: '#ef4444',
        premiumGradientTo: '#dc2626',
        premiumHoverFrom: '#dc2626',
        premiumHoverTo: '#b91c1c',
      },
    },
    'green-dark': {
      name: 'Yeşil-Koyu',
      theme: {
        bgGradientFrom: '#001a0f',
        bgGradientVia: '#0a2e1f',
        bgGradientTo: '#001a0f',
        borderColor: 'rgba(16, 185, 129, 0.2)',
        textColor: '#d1d5db',
        textHoverColor: '#ffffff',
        activeColor: '#ffffff',
        activeBgColor: 'rgba(16, 185, 129, 0.2)',
        premiumGradientFrom: '#10b981',
        premiumGradientTo: '#059669',
        premiumHoverFrom: '#059669',
        premiumHoverTo: '#047857',
      },
    },
    'orange-navy': {
      name: 'Turuncu-Lacivert',
      theme: {
        bgGradientFrom: '#0a1628',
        bgGradientVia: '#1e2a45',
        bgGradientTo: '#0a1628',
        borderColor: 'rgba(249, 115, 22, 0.2)',
        textColor: '#d1d5db',
        textHoverColor: '#ffffff',
        activeColor: '#ffffff',
        activeBgColor: 'rgba(249, 115, 22, 0.2)',
        premiumGradientFrom: '#f97316',
        premiumGradientTo: '#ea580c',
        premiumHoverFrom: '#ea580c',
        premiumHoverTo: '#c2410c',
      },
    },
  },
};

// ✅ PERFORMANCE: In-memory cache to prevent repeated DB queries
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache
const cache = new Map<string, CacheEntry<any>>();

function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  
  const now = Date.now();
  if (now - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

export function clearThemeCache(): void {
  cache.clear();
}

// ✅ PERFORMANCE: Batch load all theme settings in one query
export async function getAllThemeSettings(): Promise<{
  headerTheme: HeaderTheme;
  footerTheme: FooterTheme;
  siteIdentity: SiteIdentity;
  menuConfig: MenuConfig;
}> {
  const cacheKey = 'all_theme_settings';
  const cached = getCached<any>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_key, setting_value')
      .in('setting_key', ['header_theme', 'footer_theme', 'site_identity', 'menu_config']);

    if (error) throw error;

    const result = {
      headerTheme: defaultHeaderTheme,
      footerTheme: defaultFooterTheme,
      siteIdentity: defaultSiteIdentity,
      menuConfig: defaultMenuConfig,
    };

    if (data) {
      data.forEach((row) => {
        switch (row.setting_key) {
          case 'header_theme':
            result.headerTheme = row.setting_value as HeaderTheme;
            break;
          case 'footer_theme':
            result.footerTheme = row.setting_value as FooterTheme;
            break;
          case 'site_identity':
            result.siteIdentity = row.setting_value as SiteIdentity;
            break;
          case 'menu_config':
            result.menuConfig = row.setting_value as MenuConfig;
            break;
        }
      });
    }

    setCache(cacheKey, result);
    return result;
  } catch (error) {
    return {
      headerTheme: defaultHeaderTheme,
      footerTheme: defaultFooterTheme,
      siteIdentity: defaultSiteIdentity,
      menuConfig: defaultMenuConfig,
    };
  }
}

export async function getHeaderTheme(): Promise<HeaderTheme> {
  const cacheKey = 'header_theme';
  const cached = getCached<HeaderTheme>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'header_theme')
      .maybeSingle();

    if (error) throw error;
    const theme = data ? (data.setting_value as HeaderTheme) : defaultHeaderTheme;
    setCache(cacheKey, theme);
    return theme;
  } catch (error) {
    return defaultHeaderTheme;
  }
}

export async function getFooterTheme(): Promise<FooterTheme> {
  const cacheKey = 'footer_theme';
  const cached = getCached<FooterTheme>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'footer_theme')
      .maybeSingle();

    if (error) throw error;
    const theme = data ? (data.setting_value as FooterTheme) : defaultFooterTheme;
    setCache(cacheKey, theme);
    return theme;
  } catch (error) {
    return defaultFooterTheme;
  }
}

export async function updateHeaderTheme(theme: HeaderTheme): Promise<void> {
  cache.delete('header_theme');
  cache.delete('all_theme_settings');
  
  // Check if record exists first
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'header_theme')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: theme as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'header_theme');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'header_theme',
        setting_value: theme as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

export async function updateFooterTheme(theme: FooterTheme): Promise<void> {
  cache.delete('footer_theme');
  cache.delete('all_theme_settings');
  
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'footer_theme')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: theme as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'footer_theme');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'footer_theme',
        setting_value: theme as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

export async function getSiteIdentity(): Promise<SiteIdentity> {
  const cacheKey = 'site_identity';
  const cached = getCached<SiteIdentity>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'site_identity')
      .maybeSingle();

    if (error) throw error;
    const identity = data ? (data.setting_value as SiteIdentity) : defaultSiteIdentity;
    setCache(cacheKey, identity);
    return identity;
  } catch (error) {
    return defaultSiteIdentity;
  }
}

export async function updateSiteIdentity(identity: SiteIdentity): Promise<void> {
  cache.delete('site_identity');
  cache.delete('all_theme_settings');
  
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'site_identity')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: identity as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'site_identity');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'site_identity',
        setting_value: identity as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

// ✅ NEW: Get Menu Configuration
export async function getMenuConfig(): Promise<MenuConfig> {
  const cacheKey = 'menu_config';
  const cached = getCached<MenuConfig>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'menu_config')
      .maybeSingle();

    if (error) throw error;
    const config = data ? (data.setting_value as MenuConfig) : defaultMenuConfig;
    setCache(cacheKey, config);
    return config;
  } catch (error) {
    return defaultMenuConfig;
  }
}

// ✅ NEW: Update Menu Configuration
export async function updateMenuConfig(config: MenuConfig): Promise<void> {
  cache.delete('menu_config');
  cache.delete('all_theme_settings');
  
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'menu_config')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: config as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'menu_config');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'menu_config',
        setting_value: config as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

// ✅ NEW: Channel Order Management
export interface ChannelOrder {
  listing_id: string;
  order: number;
}

// Get Trend Channel Order
export async function getTrendChannelOrder(): Promise<ChannelOrder[]> {
  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'trend_channel_order')
      .maybeSingle();

    if (error) throw error;
    if (!data) return [];

    return (data.setting_value as ChannelOrder[]) || [];
  } catch (error) {
    console.error('Error fetching trend channel order:', error);
    return [];
  }
}

// Update Trend Channel Order
export async function updateTrendChannelOrder(order: ChannelOrder[]): Promise<void> {
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'trend_channel_order')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'trend_channel_order');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'trend_channel_order',
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

// Get Featured Channel Order
export async function getFeaturedChannelOrder(): Promise<ChannelOrder[]> {
  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'featured_channel_order')
      .maybeSingle();

    if (error) throw error;
    if (!data) return [];

    return (data.setting_value as ChannelOrder[]) || [];
  } catch (error) {
    console.error('Error fetching featured channel order:', error);
    return [];
  }
}

// Update Featured Channel Order
export async function updateFeaturedChannelOrder(order: ChannelOrder[]): Promise<void> {
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'featured_channel_order')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'featured_channel_order');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'featured_channel_order',
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

// ✅ NEW: Get Editor Picks Order
export async function getEditorPicksOrder(): Promise<ChannelOrder[]> {
  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'editor_picks_order')
      .maybeSingle();

    if (error) throw error;
    if (!data) return [];

    return (data.setting_value as ChannelOrder[]) || [];
  } catch (error) {
    console.error('Error fetching editor picks order:', error);
    return [];
  }
}

// ✅ NEW: Update Editor Picks Order
export async function updateEditorPicksOrder(order: ChannelOrder[]): Promise<void> {
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'editor_picks_order')
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'editor_picks_order');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'editor_picks_order',
        setting_value: order as any,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}

// ✅ AdSense Slot Management
export interface AdsenseSlot {
  id: string;
  name: string;
  slot_code: string; // Full HTML code from Google AdSense
  location: string; // e.g. "home_hero_bottom", "home_trend_bottom", "sidebar", etc.
  is_active: boolean;
  created_at: string;
}

export async function getAdsenseSlots(): Promise<AdsenseSlot[]> {
  const cacheKey = 'adsense_slots';
  const cached = getCached<AdsenseSlot[]>(cacheKey);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('site_theme_settings')
      .select('setting_value')
      .eq('setting_key', 'adsense_slots')
      .maybeSingle();

    if (error) throw error;
    const val = data?.setting_value as any;
    const slots = val?.slots || [];
    setCache(cacheKey, slots);
    return slots;
  } catch (error) {
    return [];
  }
}

export async function updateAdsenseSlots(slots: AdsenseSlot[]): Promise<void> {
  cache.delete('adsense_slots');
  
  const { data: existing } = await supabase
    .from('site_theme_settings')
    .select('id')
    .eq('setting_key', 'adsense_slots')
    .maybeSingle();

  const value = { slots } as any;

  if (existing) {
    const { error } = await supabase
      .from('site_theme_settings')
      .update({
        setting_value: value,
        updated_at: new Date().toISOString(),
      })
      .eq('setting_key', 'adsense_slots');
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('site_theme_settings')
      .insert({
        setting_key: 'adsense_slots',
        setting_value: value,
        updated_at: new Date().toISOString(),
      });
    if (error) throw error;
  }
}
